import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { SkeletonText } from '../../components/Skeleton';
import { useSession } from '../auth/queries';
import { SlaBadge } from '../sla/SlaBadge';
import { toneForPriorityLevel } from '../tickets/presentation';
import { useTicketList } from '../tickets/queries';

/**
 * Was auf dem eigenen Namen liegt -- F5-18, K-129.
 *
 * Die Auswertungen daneben sagen, wie es dem Betrieb geht. Diese Liste sagt,
 * was der Betrachter selbst zu tun hat -- und das ist morgens um acht die
 * dringendere Frage. Ohne sie beginnt der Arbeitstag damit, die Ticketliste
 * zu oeffnen und zwei Filter zu setzen.
 *
 * **Sortiert nach der Frist und nicht nach dem Datum.** Das aelteste Ticket
 * ist nicht das dringendste; das mit der knappsten Zusage ist es. Die
 * Sortierung uebernimmt der Server -- ``sort`` steht in der Abfrage, damit
 * nicht die erste Seite geholt und danach im Browser umsortiert wird. Was im
 * Browser sortiert wird, ist nur die erste Seite: Das dringendste Ticket
 * stuende dann womoeglich auf der zweiten.
 *
 * **"Offen" heisst hier "noch zu tun"** -- alle Zustandsgruppen ausser
 * "Gelöst" und "Geschlossen", dieselbe Abgrenzung, die die Kennzahl daneben
 * zaehlt. Zuvor stand hier die Zustandsgruppe ``open``, und die heisst "In
 * Bearbeitung": Ein frisch zugewiesenes Ticket fiel damit heraus, und die
 * Karte meldete "Nichts offen auf Ihrem Namen", waehrend die Kennzahl
 * daneben es mitzaehlte (Issue #7).
 *
 * **Ein Fehler der Abfrage wird angezeigt.** Er wurde es nicht, und das ist
 * der Grund, aus dem der eigentliche Fehler so lange unentdeckt blieb: Die
 * Karte sortierte nach einem Feld, nach dem der Server nicht sortiert, bekam
 * 422 -- und zeigte, weil die Liste dann leer ist, den Leerzustand. Sie sah
 * aus wie eine Karte, die funktioniert und nichts zu melden hat.
 *
 * Fuenf Zeilen: Wer zwoelf offene Vorgaenge hat, braucht hier keine Liste,
 * sondern die naechsten fuenf -- und den Weg in die vollstaendige.
 */
export function MyWorkCard() {
  const { t } = useTranslation();
  const session = useSession();

  const ownerId = session.data?.user_id;
  const query = useTicketList(
    {
      ...(ownerId ? { owner_id: ownerId } : {}),
      unresolved: true,
      // Aufsteigend: die naechste Frist zuerst. Vorgaenge ohne Zusage stehen
      // am Ende -- sie sind nicht die dringendsten, sondern die ohne Uhr.
      sort: 'resolution_due_at',
      limit: 5,
    },
    // Erst fragen, wenn die eigene Kennung feststeht. Ohne diese Bedingung
    // liefe die erste Abfrage ohne ``owner_id``, und eine Karte mit der
    // Ueberschrift "Meine Arbeit" zeigte kurz die Vorgaenge aller Kollegen.
    { enabled: ownerId !== undefined },
  );

  const tickets = query.data?.pages[0]?.items ?? [];

  return (
    // ``aria-label`` macht aus der Karte einen benannten Bereich: Wer mit
    // einer Sprachausgabe ueber die Startseite geht, springt damit von
    // Bereich zu Bereich, statt sich durch acht Kacheln zu hoeren.
    <section className="card" aria-label={t('dashboard.myWork')} aria-busy={query.isPending}>
      <div className="chart-card__head">
        <div className="page-header__title">
          <h2>{t('dashboard.myWork')}</h2>
        </div>
        <p className="chart-card__hint">{t('dashboard.myWorkHint')}</p>
      </div>

      {/* ``isPending`` ist bei einer angehaltenen Abfrage ebenfalls wahr --
          und solange die Sitzung laedt, ist das genau richtig: Es steht noch
          nicht fest, wessen Arbeit hier steht. */}
      {query.isPending ? <SkeletonText lines={4} /> : null}
      {query.isError ? <ErrorNotice error={query.error} /> : null}

      {!query.isPending && !query.isError && tickets.length === 0 ? (
        <EmptyState
          icon="check"
          title={t('dashboard.myWorkEmpty')}
          hint={t('dashboard.myWorkEmptyHint')}
        />
      ) : null}

      {tickets.length > 0 ? (
        <>
          <ul className="my-work">
            {tickets.map((ticket, index) => (
              <li
                key={ticket.id}
                className="my-work__row is-rising"
                // Gestaffeltes Erscheinen: Die Zeilen kommen von oben nach
                // unten und nicht alle auf einmal. 40 ms je Zeile -- bei
                // fuenf Zeilen ist die letzte nach 200 ms da, und damit
                // schneller, als jemand hinsieht. Wer reduzierte Bewegung
                // eingestellt hat, sieht sie sofort und alle.
                style={{ '--rise-delay': `${String(index * 40)}ms` } as React.CSSProperties}
              >
                <Link
                  to="/app/tickets/$ticketId"
                  params={{ ticketId: ticket.id }}
                  className="my-work__title"
                >
                  {ticket.title}
                </Link>
                <span className="my-work__meta">
                  <span className="table__number">{ticket.ticket_number}</span>
                  <Badge tone={toneForPriorityLevel(ticket.priority.level)}>
                    {ticket.priority.name}
                  </Badge>
                  <SlaBadge sla={ticket.sla} />
                </span>
              </li>
            ))}
          </ul>

          {/* Der Weg in die vollstaendige Liste. Dieselbe Einschraenkung wie
              hier -- sonst zeigte sie etwas anderes als die fuenf Zeilen
              darueber. */}
          <Link
            to="/app/tickets"
            search={{ unresolved: true, owner: 'me' }}
            className="nav__item"
            style={{ marginTop: 'var(--space-3)' }}
          >
            {t('dashboard.myWorkAll')}
          </Link>
        </>
      ) : null}
    </section>
  );
}
